import { useEffect, useId, useRef, useState } from 'react'

import type { UseTableColumnPrefsResult } from '../hooks/useTableColumnPrefs'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { IconChevronLeft, IconChevronRight, IconColumns } from './icons/TableColumnIcons'

type TableColumnSettingsProps = {
  prefs: UseTableColumnPrefsResult
  className?: string
}

export const TableColumnSettings = ({ prefs, className }: TableColumnSettingsProps) => {
  const [open, setOpen] = useState(false)
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dropTargetId, setDropTargetId] = useState<string | null>(null)
  const titleId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const { settingsColumns, isVisible, setColumnVisible, moveColumn, reorderColumn, resetPrefs } = prefs

  const dialogRef = useFocusTrap<HTMLDivElement>(open, {
    onClose: () => setOpen(false),
    triggerRef,
  })

  useEffect(() => {
    if (!open) {
      setDraggingId(null)
      setDropTargetId(null)
    }
  }, [open])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={['echat-btn echat-btn--ghost echat-btn--sm market-table-columns-btn', className]
          .filter(Boolean)
          .join(' ')}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Столбцы"
        title="Столбцы"
        onClick={() => setOpen(true)}
      >
        <IconColumns />
      </button>

      {open ? (
        <div
          className="market-dialog-backdrop"
          role="presentation"
          onClick={() => setOpen(false)}
        >
          <div
            ref={dialogRef}
            className="market-dialog market-table-columns-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id={titleId} className="market-table-columns-dialog__title">
              Столбцы
            </h2>
            <p className="market-table-columns-dialog__hint">
              Порядок слева направо — как в таблице. Перетащите или сдвиньте стрелками.
            </p>
            <ul className="market-table-columns-dialog__list" role="list">
              {settingsColumns.map((column, index) => {
                const visible = isVisible(column.id)
                const required = Boolean(column.required)
                const pinned = Boolean(column.pin)
                const isDragging = draggingId === column.id
                const isDropTarget = dropTargetId === column.id && draggingId != null && draggingId !== column.id
                return (
                  <li
                    key={column.id}
                    className={[
                      'market-table-columns-dialog__item',
                      !visible ? 'market-table-columns-dialog__item--hidden' : '',
                      isDragging ? 'market-table-columns-dialog__item--dragging' : '',
                      isDropTarget ? 'market-table-columns-dialog__item--drop-target' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    draggable={!pinned}
                    onDragStart={(event) => {
                      if (pinned) {
                        event.preventDefault()
                        return
                      }
                      setDraggingId(column.id)
                      event.dataTransfer.effectAllowed = 'move'
                      event.dataTransfer.setData('text/plain', column.id)
                    }}
                    onDragEnd={() => {
                      setDraggingId(null)
                      setDropTargetId(null)
                    }}
                    onDragOver={(event) => {
                      if (pinned || !draggingId || draggingId === column.id) {
                        return
                      }
                      event.preventDefault()
                      event.dataTransfer.dropEffect = 'move'
                      setDropTargetId(column.id)
                    }}
                    onDragLeave={() => {
                      if (dropTargetId === column.id) {
                        setDropTargetId(null)
                      }
                    }}
                    onDrop={(event) => {
                      event.preventDefault()
                      const dragId = event.dataTransfer.getData('text/plain') || draggingId
                      if (dragId && dragId !== column.id && !pinned) {
                        reorderColumn(dragId, column.id)
                      }
                      setDraggingId(null)
                      setDropTargetId(null)
                    }}
                  >
                    <label className="market-table-columns-dialog__check">
                      <input
                        type="checkbox"
                        checked={visible}
                        disabled={required}
                        onChange={(event) => setColumnVisible(column.id, event.target.checked)}
                      />
                      <span>{column.label}</span>
                    </label>
                    <div className="market-table-columns-dialog__move">
                      <button
                        type="button"
                        className="echat-btn echat-btn--ghost echat-btn--sm market-table__icon-btn"
                        aria-label={`Сдвинуть «${column.label}» влево`}
                        disabled={pinned || index === 0}
                        onClick={() => moveColumn(column.id, -1)}
                      >
                        <IconChevronLeft />
                      </button>
                      <button
                        type="button"
                        className="echat-btn echat-btn--ghost echat-btn--sm market-table__icon-btn"
                        aria-label={`Сдвинуть «${column.label}» вправо`}
                        disabled={pinned || index === settingsColumns.length - 1}
                        onClick={() => moveColumn(column.id, 1)}
                      >
                        <IconChevronRight />
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
            <div className="market-table-columns-dialog__actions">
              <button
                type="button"
                className="echat-btn echat-btn--ghost echat-btn--sm"
                onClick={() => {
                  resetPrefs()
                }}
              >
                Сбросить
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--primary echat-btn--sm"
                onClick={() => setOpen(false)}
              >
                Готово
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
